import { SearchIcon, XIcon } from '@heroicons/react/outline';
import { motion } from 'framer-motion';
import { unstable_batchedUpdates } from 'react-dom';
import { Button } from '@skipper/ui';
import { useState, useRef, ChangeEvent } from 'react';

const variants = {
    closed: {
        width: 0,
        display: 'hidden',
        opacity: 0,
    },
    open: {
        width: '100%',
        display: 'block',
        flexGrow: 1,
        opacity: 1,
        paddingLeft: 4,
        paddingRight: 4,
    },
};

const variantSearch = {
    closed: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    open: {
        alignItems: 'center',
        justifyContent: 'start',
        paddingLeft: 4,
    },
};
const variantX = {
    closed: {
        width: 0,
        display: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        opacity: 0,
    },
    open: {
        width: '30px',
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        paddingRight: 4,
        opacity: 1,
    },
};

const variantContainer = {
    closed: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '40px',
    },
    open: {
        width: '320px',
    },
};

const variantContainerSmall = {
    closed: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '32px',
    },
    open: {
        width: '320px',
    },
};

export function AnimatedSearchField({
    value,
    onChange,
    onClose,
    size = 'medium',
    placeholder,
}: {
    value?: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClose: () => void;
    size?: 'small' | 'medium';
    placeholder?: string;
}) {
    const [currentVariant, setVariant] = useState(value ? 'opened' : 'closed');
    const inputRef = useRef<HTMLInputElement | null>(null);
    return (
        <motion.div
            layout
            className={`relative flex items-center border border-gray-300 bg-white rounded-md ${
                size === 'medium' ? 'h-[40px]' : 'h-[32px]'
            }`}
            variants={size === 'medium' ? variantContainer : variantContainerSmall}
            animate={currentVariant}
        >
            <Button
                as={motion.button}
                type="button"
                animate={currentVariant}
                variant="textOnly"
                variants={variantSearch}
                onClick={() => {
                    setVariant('open');
                    requestAnimationFrame(() => {
                        inputRef.current?.focus();
                    });
                }}
                className="h-full"
            >
                <SearchIcon className="w-5 h-5 stroke-gray-500" />
            </Button>
            <motion.input
                ref={inputRef}
                initial={currentVariant}
                animate={currentVariant}
                variants={variants}
                className="h-full focus:outline-none w-full"
                value={value}
                placeholder={placeholder}
                onChange={(e) => {
                    onChange(e);
                }}
            />
            <motion.button
                initial={currentVariant}
                animate={currentVariant}
                variants={variantX}
                onClick={() => {
                    onClose();
                    setVariant('closed');
                }}
                className="h-full focus:outline-none focus:ring-none"
            >
                <XIcon className="fill-gray-500 w-5 h-5" />
            </motion.button>
        </motion.div>
    );
}
